import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

interface FieldDef {
  name: string;
  label: string;
  dependency?: string[];
}

export class DependencyParameterValidator {
  /**
   * Dependency conditional validator
   * If any trigger fields are filled, one of the dependent fields become required.
   */
  static dependencyFieldValidator(fields: FieldDef[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control || !(control instanceof FormGroup)) return null;

      const formGroup = control as FormGroup;
      const triggerFields = fields.filter(
        (field) => Array.isArray(field.dependency) && field.dependency.length
      );
      const triggerFieldNames = triggerFields.map((field) => field.name);
      const triggerFieldLabels = triggerFields.map((field) => field.label);
      const dependentFieldNames = new Set<string>();
      triggerFields.forEach((field) =>
        field.dependency?.forEach((dep) => dependentFieldNames.add(dep))
      );
      const dependentFieldLabels = Array.from(dependentFieldNames).map(
        (dn) => fields.find((fd) => fd.name === dn)?.label || dn
      );
      
      if (triggerFields.length === 0) return null;  

      // Check if any trigger field is filled
      const anyTriggerFilled = triggerFieldNames.some((fn) => {
        const value = formGroup.get(fn)?.value;
        return value !== null && value !== undefined && value !== '' && value.length > 0;
      });

      if (!anyTriggerFilled) return null;

      // Check if any dependent field is filled
      const anyDependentFilled = Array.from(dependentFieldNames).some((fn) => {
        const value = formGroup.get(fn)?.value;
        return value !== null && value !== undefined && value !== '';
      });

      if (anyDependentFilled) return null;

      // Error Message
      const triggerText = joinLabels(triggerFieldLabels);
      const dependentText = joinLabels(dependentFieldLabels);
      const message = `If selecting either ${triggerText}, please provide either ${dependentText}.`;

      return {
        dependencyError: {
          message,
          triggerFields: triggerFieldNames,
          dependentFields: Array.from(dependentFieldNames),
        },
      };
    };
  }
}

/**
 * Helper: joins field label array into a string
 */
function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] || '';
  return labels.slice(0, -1).join(', ') + ` or ${labels[labels.length - 1]}`;
}
